/**
 * work/install-card-mobile.md — the approved A3 design: a "Plan {country} in Stippl" card that
 * sits in normal document flow (not fixed, covers nothing) early in a blog article or Discover
 * page, before the reader is deep in.
 *
 * Distinct from the two install affordances these pages already carry:
 *  - `DownloadAppQrCard` — fixed, reveals after ~500px of scroll (or at the footer on tablet/
 *    mobile), floats over the page.
 *  - `WaveDownloadCTA` / `DownloadBanner` — the existing end-of-page CTA, ~80%+ page depth.
 *
 * Neither gives an early-scroll phone reader anything beyond the always-on "Get the app" button
 * already in the scrolled marketing header — see the spec's "what changed the question" section
 * for why visibility alone wasn't the gap this fills. This block is the one that is.
 *
 * Mobile/tablet only, hidden on desktop: the same `(max-width: 1024px)` breakpoint
 * `DownloadAppQrCard` uses for its own compact layout (`COMPACT_MEDIA`). Hidden with CSS
 * (`InlineInstallCard.scss`), not JS, so it ships in the server-rendered HTML and causes no
 * layout shift — a desktop visitor's browser never even requests the hidden markup's assets, and
 * there is no hydration mismatch to tear down.
 *
 * Tagged separately from every other install affordance on the same page, or its own effect could
 * never be read apart from theirs (all of them already report `c=web_blog`/`c=web_discover`):
 *  - `af_adset=inline_install_block` on the OneLink URL — see `withAdSet` in
 *    `useAppsflyerOneLink.ts`, applied here via `usePlacementOnelinkUrl` (`useAppStoreLink.ts`).
 *    Survives `withCurrentPath`'s af_sub1 rewrite because the two touch different query params.
 *  - `placement: "inline_banner"` on the `click_appstore_cta`/`click_googleplay_cta` OpenPanel
 *    events — the same two events `DownloadAppQrCard`'s own mobile button fires (see its
 *    `handleTracking`), with the canonical `TrackPlacement` value (`analytics/eventCatalog.ts`)
 *    that already distinguishes this kind of in-flow block elsewhere on these pages (e.g.
 *    `AIPlanBanner`'s own `"inline_banner"` placement in `DiscoverCountryPage.tsx`).
 *
 * SSR-rendered on both blog articles and /discover pages, so the href is resolved through
 * `usePlacementOnelinkUrl` rather than computed inline — see that hook's own doc comment
 * (`useAppStoreLink.ts`) for the pre/post-mount contract that keeps the server and first client
 * render byte-identical.
 */
import { usePlacementOnelinkUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import { getBlogTranslations } from "components/website/marketing/i18n/pages/blog";
import { t } from "i18n/index";
import { OsTypes, osName } from "react-device-detect";
import "./InlineInstallCard.scss";

// Verbatim from DownloadAppQrCard.tsx's own S_PATH — the same brand glyph, not re-derived.
const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

// AppsFlyer's aggregated reporting API groups by Adset but not by sub params beyond af_sub1
// (already spent on the page path) — see withAdSet's own doc comment for the full reasoning.
const AF_ADSET = "inline_install_block";

/** `t()` has no placeholder support — fill `{name}` style tokens after the lookup. Same tiny
 *  helper DiscoverArticle.tsx/ItineraryArticle.tsx each define locally rather than share. */
const fill = (str: string, vars: Record<string, string>) => str.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);

interface InlineInstallCardProps {
	/**
	 * Localised, display-ready place name for the title's "Plan {country} in Stippl" — the
	 * country on a country hub or a blog article that resolves one off its breadcrumbs, the
	 * destination on a city hub (matches `DownloadBanner`'s own choice to fill the same key with
	 * the destination name rather than its parent country). Omitted on content that resolves no
	 * country at all (e.g. "best AI travel planner" roundups, every legacy Strapi article) — falls
	 * back to a country-agnostic title rather than an empty or malformed one.
	 */
	country?: string;
	/**
	 * Drives this block's own copy — the article/guide's own locale, not the visitor's browser
	 * language. Every caller already resolves this the same way for its own chrome strings; see
	 * the long locale comments in DiscoverCountryPage.tsx/DiscoverDestinationPage.tsx for why
	 * `i18n.language` is the wrong read during SSR.
	 */
	locale: string;
}

const InlineInstallCard = ({ country, locale }: InlineInstallCardProps) => {
	const tr = getBlogTranslations(locale);
	// Hydration-safe attributed URL for this placement — see usePlacementOnelinkUrl's own doc
	// comment (useAppStoreLink.ts) for the pre/post-mount contract and af_adset tagging.
	const trackedUrl = usePlacementOnelinkUrl(AF_ADSET);

	const handleClick = () => {
		const isAndroid = osName === OsTypes.Android;
		// The same two OpenPanel events DownloadAppQrCard's own mobile button fires — `placement`
		// is what tells this block's taps apart from every other install affordance afterwards.
		trackEvent(isAndroid ? "click_googleplay_cta" : "click_appstore_cta", { placement: "inline_banner" }, "marketing_site");
	};

	const title = country
		? fill(t(tr, "blog.article.ctaTitle", "Plan {country} in Stippl"), { country })
		: t(tr, "guide.chrome.planItInStippl", "Plan it in Stippl");

	return (
		<div className="inline-install-card">
			<div className="inline-install-card__header">
				<span className="inline-install-card__logo">
					<svg width="12" height="16" viewBox="0 0 81 108" fill="none" aria-hidden="true">
						<path d={S_PATH} fill="#ffffff" />
					</svg>
				</span>
				<span className="inline-install-card__kicker">{t(tr, "blog.article.ctaKicker", "Ready when you are")}</span>
			</div>
			<p className="inline-install-card__title">{title}</p>
			<p className="inline-install-card__body">
				{t(tr, "guide.chrome.downloadCtaBody", "Day-by-day planning, stays and budget in one app, with every place from this guide already on the map.")}
			</p>
			<a href={trackedUrl} className="inline-install-card__button" target="_blank" rel="noreferrer" onClick={handleClick}>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
					<path d="M12 3v13M7 11l5 5 5-5" />
					<path d="M5 21h14" />
				</svg>
				<span>{t(tr, "guide.chrome.downloadApp", "Download the app")}</span>
			</a>
		</div>
	);
};

export default InlineInstallCard;
